import React from 'react';
import { SaveOutlined } from '@ant-design/icons';
import { dateTimeDisplayFormat, formatApiDateTime, ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { Col, Form, Input, InputNumber, message, Modal, Row, Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { TripStatusType } from '@/constants';
import AppDateTimePicker from '@/components/FormItems/DateTimePicker';
import { TripDetailDto, CurrentTripStationModel } from '@/__generated';
import { apiSdk } from '@/httpClient';

type Props = {
  trip: TripDetailDto;
  onClose: (reload: boolean) => void;
};
const getTripNewStatusOptions = (status: number) => {
  switch (status) {
    case TripStatusType.Scheduled:
      return [TripStatusType.CheckedIn, TripStatusType.Cancelled, TripStatusType.Delayed];
    case TripStatusType.CheckedIn:
      return [TripStatusType.Cancelled, TripStatusType.Boarding];
    case TripStatusType.Boarding:
      return [TripStatusType.Departed];
    case TripStatusType.Left:
      return [TripStatusType.Arrived, TripStatusType.Reached, TripStatusType.Interrupted];
    case TripStatusType.Departed:
      return [TripStatusType.Arrived, TripStatusType.Reached, TripStatusType.Interrupted];
    case TripStatusType.Delayed:
      return [TripStatusType.CheckedIn, TripStatusType.Cancelled];
    case TripStatusType.Reached:
      return [TripStatusType.Interrupted, TripStatusType.Left];
  }

  return [];
};
export default function UpdateStatusModal({ trip, onClose }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [stationInfo, setStationInfo] = React.useState<CurrentTripStationModel>();
  React.useEffect(() => {
    if (trip?.id) {
      apiSdk.TripDetailService.getCurrentStation({ tripDetailId: trip.id }).then(res => {
        setStationInfo(res);
      });
    }
  }, [trip]);
  const [form] = Form.useForm();

  const onSave = values => {
    setLoading(true);
    apiSdk.TripDetailService.updateTripStatus({
      body: {
        ...values,
        id: trip.id,
        actualDateTime: values.actualDateTime ? formatApiDateTime(values.actualDateTime) : undefined,
      },
    })
      .then(response => {
        if (response?.success) {
          onClose(true);
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const newStatusOptions = React.useMemo(() => {
    return getTripNewStatusOptions(trip.status || 0).map(s => ({ value: s, label: TripStatusType.__getValue(s) }));
  }, [trip]);
  const status = Form.useWatch('status', form);
  const displayActualTime = React.useMemo(() => {
    return [TripStatusType.Departed, TripStatusType.Reached, TripStatusType.Left, TripStatusType.Arrived].includes(status);
  }, [status]);

  return (
    <Modal
      open={true}
      maskClosable={false}
      width={ModalWidth.small}
      className="nta-custom-form"
      title={trip.tripRefNumber}
      forceRender={true}
      style={{ top: 10 }}
      okText={t('save')}
      cancelText={newStatusOptions.length ? t('cancel') : t('Close')}
      okButtonProps={{
        icon: <SaveOutlined />,
        className: 'save-btn',
        loading: loading,
        style: { display: newStatusOptions.length ? undefined : 'none' },
      }}
      onCancel={() => {
        onClose(false);
      }}
      onOk={() => {
        form
          .validateFields()
          .then(onSave)
          .catch(err => console.log(err));
      }}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
          <Row gutter={ROW_GUTTER}>
            <Col md={24}>
              <Form.Item label={t('tripDetailsPage_TripRefNumber')}>
                <Input value={trip.tripRefNumber} className="bold input-value-color" disabled />
              </Form.Item>
              <Form.Item label={t('tripDetailsPage_TripName')}>
                <Input value={trip.tripName} className="bold input-value-color" disabled />
              </Form.Item>
              <Form.Item label={t('tripDetailsPage_DepartureDate')}>
                <Input value={dateTimeDisplayFormat.originDateTime(trip.plannedDepartureDateTime)} className="bold input-value-color" disabled />
              </Form.Item>
              <Form.Item label={t('tripDetailsPage_Status')}>
                <Input value={TripStatusType.__getValue(trip.status)} className="bold input-value-color" disabled />
              </Form.Item>
              <Form.Item label={t('tripDetailsPage_CurrentStatus')}>
                <Input value={stationInfo?.currentStation} className="bold input-value-color" disabled />
              </Form.Item>
              <Form.Item label={t('tripDetailsPage_NextStation')}>
                <Input value={stationInfo?.nextStation} className="bold input-value-color" disabled />
              </Form.Item>

              {newStatusOptions.length ? (
                <>
                  <Form.Item label={t('tripDetailsPage_NewStatus')} name="status" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                    <Select options={newStatusOptions} />
                  </Form.Item>
                  {status === TripStatusType.Delayed ? (
                    <Form.Item
                      label={t('tripDetailsPage_DelayedTime')}
                      name="delayedMinutes"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <InputNumber min={0} addonAfter={t('Minutes')} className="w-100" />
                    </Form.Item>
                  ) : null}
                  {displayActualTime ? (
                    <Form.Item label={t('tripDetailsPage_ActualTime')} name="actualDateTime" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <AppDateTimePicker className="w-100" />
                    </Form.Item>
                  ) : null}
                  <Form.Item label={t('tripDetailsPage_Remarks')} name="remark">
                    <Input.TextArea />
                  </Form.Item>
                </>
              ) : null}
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}
